/**
 * HL Holdings Application
 * 메인 애플리케이션 클래스
 */

import { Drawer } from "./components/Drawer.js";
import { Header } from "./components/Header.js";
import { Popup } from "./components/Popup.js";
import { Pagination } from "./components/Pagination.js";
import { MainHero } from "./components/MainHero.js";
import { MainPofol } from "./components/MainPofol.js";
import { MainResource } from "./components/MainResource.js";
import { SubNav } from "./components/SubNav.js";
import { FloatMenu } from "./components/FloatMenu.js";
import { Captcha } from "./components/Captcha.js";
import { GoToTop } from "./components/GoToTop.js";
import { BaseFaq } from "./components/BaseFaq.js";
import { BeforeAfterFaq } from "./components/BeforeAfterFaq.js";
import { Marquee } from "./components/Marquee.js";
import { Inquiry } from "./components/Inquiry.js";
import { NewsResources } from "./components/NewsResources.js";
import { MainAnimation } from "./MainAnimation.js";
import { SubPage } from "./SubPage.js";
import { GlobalTransportPage } from "./GlobalTransportPage.js";
import BrandPage from "./components/BrandPage.js";
import { ConsultingIntro } from "./components/ConsultingIntro.js";
import { PageNav } from "./components/PageNav.js";
import { MainVisualBackgroundEffect } from "./components/MainVisualBackgroundEffect.js";
import { initSplashIntro } from "./components/SplashIntro.js";
import { MainPage } from "./pages/MainPage.js";

export class Application {
    constructor() {
        this.components = {};
        this.mainAnimation = new MainAnimation();
        this.subPage = new SubPage();
        this.globalTransportPage = new GlobalTransportPage();
        this.init();
    }

    init() {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () =>
                this.initComponents(),
            );
        } else {
            this.initComponents();
        }
    }

    initComponents() {
        console.log("Application: Initializing components...");

        // 인트로 스플래시 (메인 페이지만, 쿠키/FORCE_SHOW_INTRO 적용)
        if (document.getElementById("splash-mask")) {
            initSplashIntro();
        }

        // Lenis 초기화
        this.initLenis();

        // 컴포넌트 초기화
        try {
            // 기본 컴포넌트
            this.components.pagination = new Pagination();

            // 조건부 컴포넌트
            const footerTopBtn = document.getElementById("footer-top-btn");
            if (footerTopBtn) {
                footerTopBtn.addEventListener("click", () => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                });
            }

            if (document.getElementById("main-drawer")) {
                this.components.drawer = new Drawer();
            }

            if (document.getElementById("main-header")) {
                this.components.header = new Header();
            }

            if (document.querySelector(".main-popup-wrap, .no-popup")) {
                this.components.popup = new Popup();
            }

            // 기타 컴포넌트 (필요시)
            if (document.querySelector(".no-main-hero__container")) {
                this.components.mainHero = new MainHero();
            }
            this.components.mainPofol = new MainPofol();
            this.components.mainResource = new MainResource();
            this.components.subNav = new SubNav();
            this.components.floatMenu = new FloatMenu();
            this.components.captcha = new Captcha();
            this.components.goToTop = new GoToTop();
            this.components.baseFaq = new BaseFaq();
            this.components.beforeAfterFaq = new BeforeAfterFaq();
            this.components.marquee = new Marquee();
            this.components.inquiry = new Inquiry();
            this.components.brand = new BrandPage();

            if (document.querySelector(".no-news-resources__slider")) {
                this.components.newsResources = new NewsResources();
            }

            // Consulting Intro 인터랙션
            if (document.querySelector(".no-consulting-intro")) {
                this.components.consultingIntro = new ConsultingIntro();
            }

            // 페이지 내 좌측 네비 (HR Audit 등)
            if (document.querySelector(".no-page-nav")) {
                this.components.pageNav = new PageNav();
            }

            // 메인 비주얼 Swiper (index 페이지, 슬라이드 추후 추가 가능)
            this.initMainVisualSwiper();

            // 메인 비주얼 배경 이펙트 (은은한 퍼짐 + 드리프트). 되돌리기: this.components.mainVisualBackgroundEffect?.destroy()
            const mainVisualEffect = new MainVisualBackgroundEffect();
            if (mainVisualEffect.init()) {
                this.components.mainVisualBackgroundEffect = mainVisualEffect;
            }

            // 비밀번호 보호 링크 클릭 (News & Resources)
            this.initBoardPasswordLinks();
        } catch (error) {
            console.error("Application: Error initializing components", error);
        }

        // 메인(index) 페이지 전용: 한자 히어로 GSAP, NEWS 비밀글, About 패럴랙스
        if (document.getElementById("hero-title-svg")) {
            MainPage.init();
        }

        // MainAnimation 초기화 (메인 페이지만)
        if (document.querySelector(".no-sub-hero") === null) {
            this.initMainAnimations();
        }

        // SubPage 초기화 (서브 페이지만)
        if (document.querySelector(".no-sub-hero")) {
            this.initSubPageAnimations();
        }
        // AOS 초기화 (ScrollTrigger 이후에 초기화)
        this.initAOS();
    }

    initSubPageAnimations() {
        // 서브 페이지 Hero 애니메이션 초기화
        this.subPage.hero();

        // GlobalTransportPage 초기화 (transport-module-section이 있는 경우)
        if (document.getElementById("transport-module-section")) {
            this.globalTransportPage.init();
        }

        // ScrollTrigger refresh
        if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.refresh();
        }
    }

    initMainAnimations() {
        // 섹션 순서대로 초기화 (HTML 순서와 일치: hero → interview → advantages → solutions → network → logoMarquee)
        // 1. Hero 섹션 클립패스 애니메이션 초기화
        this.mainAnimation.hero();

        // 2. Interview 슬라이더 초기화
        this.mainAnimation.interview();

        // 3. Advantages 섹션 스크롤 트리거 초기화
        this.mainAnimation.advantage();

        // 4. Solutions 섹션 스냅 스크롤 + 클립패스 전환 초기화
        this.mainAnimation.solution();

        // 5. Global Network 섹션 애니메이션 초기화
        this.mainAnimation.network();

        // 6. Logo Marquee 초기화
        this.mainAnimation.logoMarquee();

        // 모든 ScrollTrigger 초기화 후 refresh
        if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.refresh();

            // ScrollTrigger refresh 후 AOS도 갱신
            setTimeout(() => {
                if (typeof AOS !== "undefined") {
                    AOS.refresh();
                }
            }, 200);
        }
    }

    initLenis() {
        if (typeof window.Lenis !== "undefined") {
            const lenis = new window.Lenis();

            // 전역으로 저장 (AOS에서 사용)
            window.lenis = lenis;
            if (this.components) {
                this.components.lenis = lenis;
            }

            // AOS refresh throttle (성능 최적화)
            let aosRefreshTimer = null;
            const aosRefreshThrottle = () => {
                if (aosRefreshTimer) return;
                aosRefreshTimer = requestAnimationFrame(() => {
                    if (typeof AOS !== "undefined") {
                        AOS.refresh();
                    }
                    aosRefreshTimer = null;
                });
            };

            lenis.on("scroll", () => {
                if (typeof ScrollTrigger !== "undefined") {
                    ScrollTrigger.update();
                }

                // AOS 갱신 (throttle 적용)
                aosRefreshThrottle();
            });

            if (typeof gsap !== "undefined") {
                gsap.ticker.add((time) => {
                    lenis.raf(time * 1000);
                });
                gsap.ticker.lagSmoothing(0);
            }
        }
    }

    initMainVisualSwiper() {
        if (typeof window.Swiper === "undefined") return;
        const el = document.getElementById("main-visual-swiper");
        if (!el) return;
        new window.Swiper("#main-visual-swiper", {
            // loop: true,
            speed: 1000,
            autoplay: { delay: 5000, disableOnInteraction: false },
            pagination: { el: ".no-main-visual__pagination", clickable: true },
            allowTouchMove: true,
        });
    }

    initBoardPasswordLinks() {
        document.addEventListener("click", (e) => {
            const link = e.target.closest(".no-news-resources__link-password");
            if (!link) return;
            e.preventDefault();
            const postId = link.getAttribute("data-post-id");
            const viewRoute = link.getAttribute("data-view-route");
            if (postId && typeof window.openBoardPasswordModal === "function") {
                window.openBoardPasswordModal(postId, viewRoute);
            }
        });
    }

    initAOS() {
        if (typeof AOS === "undefined") return;

        // AOS 초기화 옵션 설정
        // Lenis와 ScrollTrigger와 호환되도록 설정
        AOS.init({
            duration: 1000,
            easing: "ease-out-cubic",
            once: true,
            mirror: false,
            // Lenis 사용 시 스크롤 이벤트를 수동으로 처리
            useClassNames: false,
            // PC에서만 활성화 (선택사항 - 필요시 주석 해제)
            // disable: window.innerWidth < 1025 ? "mobile" : false,
            // offset 설정 (ScrollTrigger pin 섹션 고려)
            offset: 100,
        });

        // ScrollTrigger refresh 후 AOS도 갱신
        if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.addEventListener("refresh", () => {
                // 약간의 지연 후 AOS 갱신 (DOM 업데이트 대기)
                setTimeout(() => {
                    if (typeof AOS !== "undefined") {
                        AOS.refresh();
                    }
                }, 150);
            });
        }

        // 리사이즈 시 AOS 갱신
        let resizeTimer;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                if (typeof AOS !== "undefined") {
                    AOS.refresh();
                }
            }, 250);
        });

        // 초기 로드 후 한 번 더 갱신 (이미지 로드 등 대기)
        setTimeout(() => {
            if (typeof AOS !== "undefined") {
                AOS.refresh();
            }
        }, 500);
    }
}
